/**
 * Commerce Store
 *
 * This store contains read-only getters that derive state from both the cart and catalog stores.
 * It exists to prevent circular dependencies between cart.ts and catalog.ts.
 *
 * IMPORTANT: This store should only contain getters, not state or actions.
 * All getters here are read-only derivations that combine data from multiple stores.
 */
import { getUCBackgroundItem, useCartStore } from '@/stores/cart'
import { useCatalogStore } from '@/stores/catalog'
import { useMainStore } from '@/stores/main'
import { defineStore } from 'pinia'

import { orderBy } from 'lodash-es'

import { substitute } from '@/composables/contentful'
import {
  getPromptDefaultValue,
  promptIsCheckboxListPrompt,
  promptIsDropdownPrompt,
  promptIsImagePrompt,
  promptIsTextPrompt
} from '@/lib/customizations'
import { featureFlags } from '@/lib/featureFlags'
import { bundleHasSheetSelectionGroup } from '@/lib/packageBuilder'
import { upgradeableCartBundles } from '@/lib/selectionUpgrade'
import { routeName } from '@/router/routeName'

import type { ActiveUpsell } from '@/types/ActiveUpsell'
import { CadyMarket } from '@/types/CadyMarket'
import { CartBundleSelectionItemData } from '@/types/CartBundleSelection'
import {
  CartItemCustomization,
  CartItemValuePair,
  MultiItemCustomization,
  PromptType as LocalPromptType,
  PromptValuePair,
  SingleItemCustomization
} from '@/types/CartItemCustomization'
import type { CartItemsDetails } from '@/types/CartItemsDetails'
import { CartItemSelection } from '@/types/CartItemSelection'
import { CatalogFlowStep } from '@/types/CatalogFlowStep'
import type { CatalogFlowStepCartBundles } from '@/types/CatalogFlowStepCartBundles'
import {
  type CommerceStep,
  PACKAGE_BUILDER_STEP_ID,
  PostCatalogStepId,
  PreCatalogStepId
} from '@/types/CommerceStep'
import type { InCartUpsell } from '@/types/InCartUpsell'
import type { SegmentationKey } from '@/types/SegmentationKey'
import type { YearbookContext } from '@/types/YearbookContext'

import { SessionTypes } from '@VelocitySquared/cady-sunny16-commerce-integration-models/core-api'
import { PromptType } from '@VelocitySquared/sunny16-commerce-dataobjects/enums'
import type { AttributeDto } from '@VelocitySquared/sunny16-commerce-dataobjects/shared'
import type {
  CartBundleStorefrontDto,
  CartItemStorefrontDto,
  DropdownPromptDto,
  ImagePromptDto,
  ImagePromptResponseDto,
  MediaReferenceDto,
  PromptDto,
  PromptResponseDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

import { commerceStepsContent } from '@/content/commerceSteps'

const SENIOR_RETOUCHING_ELIGIBLE_SESSIONS: ReadonlySet<SessionTypes> = new Set([
  SessionTypes.LegacySession,
  SessionTypes.StandardSession,
  SessionTypes.SimpleSession,
  SessionTypes.Yearbook
])

/**
 * True when a catalog bundle exposes at least one selectable (non-`isIncluded`)
 * item group — i.e. the customer has a selection to make to complete the bundle
 * (e.g. Paper Types). Shared by `cartBundlesRequiringConfiguration` and
 * `underclassCartShouldSelectPoses` so the two gates cannot drift.
 */
function catalogBundleHasSelectableItemGroup(
  catalogBundle?: { bundle?: { itemGroups: { isIncluded: boolean }[] } | null } | null
): boolean {
  return catalogBundle?.bundle?.itemGroups.some((ig) => !ig.isIncluded) || false
}

export const useCommerceStore = defineStore('commerce', {
  getters: {
    /**
     * Returns cart bundles that require configuration (pose selection, customizations).
     * Excludes retouching bundles which have their own dedicated flow.
     */
    cartBundlesRequiringConfiguration(): CartBundleStorefrontDto[] {
      const cartStore = useCartStore()
      const catalogStore = useCatalogStore()

      const retouchingCatalogBundleId = catalogStore.retouchingBundle?.catalogBundleId
      const poseBackgroundSelectionBundleId = catalogStore.poseBackgroundSelectionBundleId

      return (
        cartStore.cartData?.cartBundles.filter((cartBundle) => {
          // Exclude retouching bundles
          if (
            retouchingCatalogBundleId &&
            cartBundle.catalogBundleId === retouchingCatalogBundleId
          ) {
            return false
          }

          // Exclude the pose/background selection bundle. Like retouching, it
          // has its own dedicated catalog flow step (rendered via
          // PoseBackgroundSelectionBundle) where the background is chosen, so it
          // must not re-surface as a generic radio selection on the Select Poses
          // cart-customization UI. Matched on bundleId (not catalogBundleId) to
          // mirror `poseBackgroundSelectBundleInCart`.
          if (
            poseBackgroundSelectionBundleId &&
            cartBundle.bundleId === poseBackgroundSelectionBundleId
          ) {
            return false
          }

          // find the catalog bundle related to the cart bundle
          const relatedCatalogBundle = catalogStore.catalogBundlesWithContent.find(
            (cbwc) => cbwc.catalogBundleId === cartBundle.catalogBundleId
          )

          // cart item has some customizations if:
          // - any cart item in the bundle has a canvas layout
          // - any cart item in the bundle has valid inline image prompts
          const cartBundleHasCustomizations = cartBundle.cartItems.some((cartItem) => {
            const cartItemHasValidInlinePrompts =
              cartItem.item?.prompts && cartItem.item?.prompts.some((cp) => promptIsImagePrompt(cp))

            return cartItem.item?.canvasLayout !== null || cartItemHasValidInlinePrompts
          })

          // catalog bundle has selectable items if:
          // - any item group in the bundle is not included (meaning the user can select items from it)
          const catalogBundleHasSelectableItems =
            catalogBundleHasSelectableItemGroup(relatedCatalogBundle)

          return cartBundleHasCustomizations || catalogBundleHasSelectableItems
        }) || []
      )
    },

    /**
     * Newly granted reward bundles that actually have something for the customer to configure.
     *
     * `cartStore.newBundlesNeedingConfiguration` reports what the *server* flagged via
     * `configurationRequiredCartBundleIds`, and that flag can name a reward with nothing to collect:
     * a digitals package whose only item carries no prompts, and whose item groups are all
     * `isIncluded`, has no prompt to answer and no item to pick. `CartConfigData` builds no card for
     * such a bundle, so a claim modal opened for one is empty apart from its static copy.
     *
     * So the server's flag is intersected with `cartBundlesRequiringConfiguration`, which answers the
     * same question from the cart and catalog data the client can see. A reward that clears both is
     * one the claim modal can actually render.
     *
     * This is also what decides *which* celebration a reward-granting add gets: a reward that lands
     * here is claimed in the modal, and one that does not is celebrated by the flyout instead. The
     * two are mutually exclusive, so both sides read this one getter.
     */
    rewardBundlesRequiringConfiguration(): CartBundleStorefrontDto[] {
      const cartStore = useCartStore()

      const configurableCartBundleIds = new Set(
        this.cartBundlesRequiringConfiguration.map((cartBundle) => cartBundle.cartBundleId)
      )

      return cartStore.newBundlesNeedingConfiguration.filter((cartBundle) =>
        configurableCartBundleIds.has(cartBundle.cartBundleId)
      )
    },

    /**
     * Determines whether the cart requires pose/image selection before checkout.
     *
     * For Underclass market: Delegates to `underclassCartShouldSelectPoses` which
     * checks if any cart items have image prompts that need to be filled.
     *
     * For other markets: Returns true if the cart contains bundles that require
     * customizations (image/text prompts), excluding retouching bundles which
     * have their own separate configuration flow.
     */
    cartShouldSelectPoses(): boolean {
      const mainStore = useMainStore()
      const cartStore = useCartStore()
      const catalogStore = useCatalogStore()

      if (mainStore.cadyMarket === CadyMarket.Underclass) {
        return this.underclassCartShouldSelectPoses
      }

      if (!cartStore.cartHasBundles) {
        return false
      }

      if (this.cartBundlesRequiringConfiguration.length > 0) {
        // Retouching bundles don't count toward pose selection requirement
        // since they have their own dedicated configuration step
        if (catalogStore.retouchingBundle) {
          const bundlesRequiringPoseSelection = this.cartBundlesRequiringConfiguration.filter(
            (cartBundle) =>
              cartBundle.catalogBundleId !== catalogStore.retouchingBundle?.catalogBundleId
          )
          return bundlesRequiringPoseSelection.length > 0
        }

        return true
      }

      return false
    },

    /**
     * Checks if all poses have been selected for cart items that require them.
     */
    cartHasAllPosesSelected(): boolean {
      const cartStore = useCartStore()
      const catalogStore = useCatalogStore()

      if (!cartStore.cartData || !this.cartShouldSelectPoses) {
        return true
      }

      const retouchingCatalogBundleId = catalogStore.retouchingBundle?.catalogBundleId

      // Check if all cart items have matching prompt counts and response counts
      // Exclude retouching bundles as they have optional prompts
      const allItemsHaveAllResponses = cartStore.cartData.cartBundles
        .filter((cb) => cb.catalogBundleId !== retouchingCatalogBundleId)
        .every((cartBundle) =>
          cartBundle.cartItems.every(
            (cartItem) =>
              cartItem.item?.prompts.length ===
              cartItem.cartItemCustomizationDetails.promptResponses.length
          )
        )

      return allItemsHaveAllResponses
    },

    /**
     * Groups cart bundles by their catalog flow step for display purposes.
     */
    bundlesGroupedByCatalogFlowStep(): (
      excludedCartBundleIds: string[]
    ) => CatalogFlowStepCartBundles[] {
      return (excludedCartBundleIds: string[]) => {
        const cartStore = useCartStore()
        const catalogStore = useCatalogStore()

        // Extracts the cart bundles from the cart data and groups them by catalog flow step
        if (!cartStore.cartData) return []

        const result: CatalogFlowStepCartBundles[] = []

        // Stores cart bundle ids that have already been included in the result
        const alreadyIncludedCartBundleIds: string[] = []

        catalogStore.catalogFlowStepsCombined.forEach((flowStep: CatalogFlowStep) => {
          // Filter the cart bundles that are in the flow step (bundles and upsellBundles)
          const flowStepCartBundles = cartStore.cartData?.cartBundles.filter(
            (cartBundle) =>
              [...flowStep.bundles, ...flowStep.upsellBundles]
                .map((bundle) => bundle.catalogBundleId)
                .includes(cartBundle.catalogBundleId) &&
              // Check if the cart bundle has already been included in the result
              !alreadyIncludedCartBundleIds.includes(cartBundle.cartBundleId) &&
              !excludedCartBundleIds.includes(cartBundle.catalogBundleId)
          )

          if (flowStepCartBundles && flowStepCartBundles.length > 0) {
            // Add the cart bundle IDs to the "already in result" tracking array
            alreadyIncludedCartBundleIds.push(...flowStepCartBundles.map((b) => b.cartBundleId))

            const item: CatalogFlowStepCartBundles = {
              bundles: flowStepCartBundles,
              flowStep,
              commerceStep: this.commerceAndCatalogSteps.find(
                (cs: CommerceStep) =>
                  cs.catalogFlowStepId === flowStep.flowStepData?.catalogFlowStepId
              ) as CommerceStep
            }

            result.push(item)
          }
        })

        return result
      }
    },

    /**
     * Returns detailed cart items information grouped by catalog flow step.
     */
    cartItemsDetails(): CartItemsDetails | null {
      const cartStore = useCartStore()
      const catalogStore = useCatalogStore()

      if (!cartStore.cartData || !cartStore.cartHasBundles) return null

      const inCartUpsellBundles: CartBundleStorefrontDto[] =
        cartStore.cartData?.cartBundles.filter((cb) => {
          return cb.attributes?.some(
            (a: AttributeDto) => a.key === 'Source' && a.value === 'AutomaticUpsell'
          )
        }) || []

      // One row per catalog bundle Sunny16 auto-applies, whether or not it is in the cart right
      // now: the row is anchored on the catalog bundle so it can still be drawn, unchecked, after
      // the customer removes it. A live upsell bundle whose catalog bundle is not in the offered
      // set is appended if its catalog bundle resolves at all, and otherwise falls through to the
      // ungrouped list below rather than disappearing from the order.
      //
      // The offer is withheld while a suppressor item is in the cart, mirroring the check
      // `/prepare` makes before inserting: a row for something Sunny16 would refuse to add would
      // be a checkbox that does nothing. A live upsell bundle still renders through the fallback.
      const liveUpsellFor = (catalogBundleId: string): CartBundleStorefrontDto | null =>
        inCartUpsellBundles.find((cb) => cb.catalogBundleId === catalogBundleId) ?? null

      const suppressorItemIds = new Set(catalogStore.inCartUpsellSuppressorItemIds)
      const upsellIsSuppressed = cartStore.cartData.cartBundles.some((cb) =>
        cb.cartItems.some((ci) => ci.item !== null && suppressorItemIds.has(ci.item.itemId))
      )

      const offeredCatalogBundles = upsellIsSuppressed
        ? []
        : catalogStore.inCartUpsellCatalogBundles
      const offeredCatalogBundleIds = new Set(offeredCatalogBundles.map((cb) => cb.catalogBundleId))

      const inCartUpsells: InCartUpsell[] = offeredCatalogBundles.map((catalogBundle) => ({
        catalogBundle,
        cartBundle: liveUpsellFor(catalogBundle.catalogBundleId)
      }))

      inCartUpsellBundles
        .filter((cb) => !offeredCatalogBundleIds.has(cb.catalogBundleId))
        .forEach((cartBundle) => {
          const catalogBundle = catalogStore.catalogData?.catalogBundles?.find(
            (cb) => cb.catalogBundleId === cartBundle.catalogBundleId
          )
          if (catalogBundle) inCartUpsells.push({ catalogBundle, cartBundle })
        })

      const inCartUpsellCatalogBundleIds = inCartUpsells
        .filter((u) => u.cartBundle !== null)
        .map((u) => u.catalogBundle.catalogBundleId)

      const bundlesGrouped = this.bundlesGroupedByCatalogFlowStep(inCartUpsellCatalogBundleIds)

      const groupedCatalogBundleIds = bundlesGrouped.flatMap((catalogFlowStepCartBundles) =>
        catalogFlowStepCartBundles.bundles.map((cfsb) => cfsb.catalogBundleId)
      )

      const ungroupedCartBundles = cartStore.cartData?.cartBundles.filter(
        (cb) =>
          !groupedCatalogBundleIds.includes(cb.catalogBundleId) &&
          !inCartUpsellCatalogBundleIds.includes(cb.catalogBundleId)
      )

      return {
        catalogFlowStepCartBundles: bundlesGrouped,
        ungroupedCartBundles,
        inCartUpsells
      }
    },

    /**
     * Checks if the pose background selection bundle is in the cart.
     */
    poseBackgroundSelectBundleInCart(): boolean {
      const cartStore = useCartStore()
      const catalogStore = useCatalogStore()

      const poseBackgroundSelectionBundleId = catalogStore.poseBackgroundSelectionBundleId

      if (!poseBackgroundSelectionBundleId) {
        return false // if there's no pose background selection bundle in the catalog, return false
      }

      if (!cartStore.cartHasBundles) {
        return false // if cart has no bundles there's definitely no background bundle in the cart
      }

      return (
        cartStore.cartData?.cartBundles.some(
          (cb) => cb.bundleId === poseBackgroundSelectionBundleId
        ) || false
      )
    },

    /**
     * For Underclass market: checks if any cart bundle still needs configuration
     * before checkout — either a customizable prompt (Wallet Personalization) or
     * a selectable (non-`isIncluded`) item group the customer must choose from
     * (e.g. Paper Types). Excludes bundles with their own dedicated flow
     * (retouching, pose/background selection), mirroring
     * `cartBundlesRequiringConfiguration`.
     */
    underclassCartShouldSelectPoses(): boolean {
      const mainStore = useMainStore()
      const cartStore = useCartStore()
      const catalogStore = useCatalogStore()

      if (mainStore.cadyMarket !== CadyMarket.Underclass) {
        return false
      }

      if (!cartStore.cartHasBundles) {
        return false
      }

      const retouchingCatalogBundleId = catalogStore.retouchingBundle?.catalogBundleId
      const poseBackgroundSelectionBundleId = catalogStore.poseBackgroundSelectionBundleId

      return (
        cartStore.cartData?.cartBundles.some((cartBundle) => {
          const sourceCatalogBundle = catalogStore.catalogData?.catalogBundles?.find(
            (cb) => cb.catalogBundleId === cartBundle.catalogBundleId
          )

          if (!sourceCatalogBundle) {
            return false
          }

          // Exclude bundles with their own dedicated flow (matches
          // `cartBundlesRequiringConfiguration`): retouching has its own step,
          // and the pose/background-selection bundle is chosen in the catalog
          // flow (matched on bundleId, like `poseBackgroundSelectBundleInCart`).
          const isRetouchingBundle =
            !!retouchingCatalogBundleId && cartBundle.catalogBundleId === retouchingCatalogBundleId
          const isPoseBackgroundBundle =
            !!poseBackgroundSelectionBundleId &&
            cartBundle.bundleId === poseBackgroundSelectionBundleId

          if (isRetouchingBundle || isPoseBackgroundBundle) {
            return false
          }

          // A selectable (non-included) item group — e.g. Paper Types — is a
          // selection the customer must make to complete the bundle, so it
          // surfaces the Select Poses step just like a customizable prompt.
          if (catalogBundleHasSelectableItemGroup(sourceCatalogBundle)) {
            return true
          }

          const sourceCatalogBundleCustomizablePromptIds = (
            sourceCatalogBundle.bundle?.itemGroups ?? []
          )
            .filter((ig) => ig.isIncluded)
            .flatMap((ig) =>
              (ig.itemGroupItems ?? []).flatMap((i) =>
                (i.item?.prompts ?? [])
                  .filter((p) => p.promptType !== PromptType.ImagePrompt || i.item?.canvasLayout)
                  .map((p) => p.promptId)
              )
            )
          return sourceCatalogBundleCustomizablePromptIds.length > 0
        }) || false
      )
    },

    /**
     * Checks if the cart contains the retouching bundle.
     */
    cartContainsRetouchingBundle(): boolean {
      const cartStore = useCartStore()
      const catalogStore = useCatalogStore()

      if (!catalogStore.retouchingBundle) {
        return false
      }

      return (
        cartStore.cartData?.cartBundles.some(
          (cb) => cb.catalogBundleId === catalogStore.retouchingBundle?.catalogBundleId
        ) || false
      )
    },

    /**
     * Whether the cart contains at least one bundle that carries a Package
     * Builder (SheetSelection) item group. Drives the existence gate for the
     * dedicated Package Builder step — mirroring how `cartShouldSelectPoses`
     * gates the Select Poses step on cart contents.
     */
    cartHasPackageBuilderBundles(): boolean {
      const cartStore = useCartStore()
      const catalogStore = useCatalogStore()

      if (!cartStore.cartHasBundles) return false

      return (
        cartStore.cartData?.cartBundles.some((cartBundle) => {
          const relatedCatalogBundle = catalogStore.catalogBundlesWithContent.find(
            (cbwc) => cbwc.catalogBundleId === cartBundle.catalogBundleId
          )
          return bundleHasSheetSelectionGroup(relatedCatalogBundle?.bundle)
        }) || false
      )
    },

    /**
     * Whether a cart row offers a remove control.
     *
     * A getter rather than a per-component computed because three surfaces render one and they
     * must not disagree: `CartBundleItem` (cart page), `CartConfigBundle` (configure) and
     * `AppCartSidebarBundleItem` (cart flyout).
     *
     * Price is deliberately not consulted — the two components that keyed on a non-zero total were
     * using it as a proxy for the cases below, and the background bundle is priced on some
     * catalogs and free on others. Rationale:
     * `docs/features/2026.09.18-cart-bundle-removability/implementation-plans/2026.09.18-001-cart-bundle-removability.md`.
     */
    cartBundleIsRemovable(): (cartBundle: CartBundleStorefrontDto) => boolean {
      return (cartBundle: CartBundleStorefrontDto): boolean => {
        // A dependent bundle leaves with its parent, never on its own.
        if (cartBundle.requiredParentCartBundleId !== null) {
          return false
        }

        // A promotion reward is not the customer’s to remove: it arrives and leaves with the
        // qualifying subtotal.
        if (cartBundle.sourcePromotionId) {
          return false
        }

        // The background selection is required for an underclass order to complete — the customer
        // changes it on the catalog flow step, never removes it. Removing it was close to
        // meaningless anyway: `PoseBackgroundSelectionBundle` re-adds a default whenever the step
        // renders, leaving that step holding a dead `cartBundleId` its Continue then posted.
        if (useCatalogStore().isPoseBackgroundSelectionBundleId(cartBundle.bundleId)) {
          return false
        }

        return true
      }
    },

    /**
     * The selections a customer made on a cart bundle — the cart items belonging to a
     * non-`isIncluded` item group — as the label/value pairs a cart row displays.
     *
     * A getter rather than a per-component computed because two rows render this and they must not
     * word a selection differently: `CartBundleItem` on the checkout/order screens and
     * `AppCartSidebarBundleItem` in the cart flyout.
     */
    cartBundleSelections(): (cartBundleId: string) => CartBundleSelectionItemData[] {
      return (cartBundleId: string) => {
        const cartStore = useCartStore()
        const result: CartBundleSelectionItemData[] = []

        const cartBundle = cartStore.cartData?.cartBundles.find(
          (cartBundle) => cartBundle.cartBundleId === cartBundleId
        )

        if (!cartBundle) {
          return result
        }

        const catalogStore = useCatalogStore()

        // `catalogData` is nullable and the cart outlives it: `clearCatalogData()` drops
        // the catalog on its own (a catalog-less preorder context) while the persisted
        // cart keeps its bundles, and this getter runs on every render — the templates'
        // own `v-if` invokes it. Asserting non-null here threw for those carts.
        const catalogBundle = catalogStore.catalogData?.catalogBundles?.find(
          (cb) => cb.catalogBundleId === cartBundle.catalogBundleId
        )

        if (!catalogBundle) {
          return result
        }

        cartBundle.cartItems.forEach((ci) => {
          // get the item group for the cart item
          const cartItemItemGroup = catalogBundle.bundle?.itemGroups.find((ig) =>
            ig.itemGroupItems?.some((igi) => igi.itemId === ci.itemId)
          )

          // if the item group is found and is not included it's a selection
          if (cartItemItemGroup && !cartItemItemGroup.isIncluded) {
            // find the item group item for the cart item
            const cartItemItemGroupItem = cartItemItemGroup.itemGroupItems?.find(
              (igi) => igi.itemId === ci.itemId
            )

            if (cartItemItemGroupItem) {
              // get the catalog content for the item group
              const itemGroupContent = catalogStore.catalogContent?.catalogItemGroups?.find(
                (cig) => cig.sunny16Guid === cartItemItemGroup!.itemGroupId
              )

              // get the catalog content for the item
              const itemContent = catalogStore.catalogItemContent(cartItemItemGroupItem.itemId)

              // `commerceItemGroupDetail` is a Contentful reference, and an unpublished
              // link resolves to nothing — so it is absent at runtime despite being
              // typed as required. `catalogContent` is `any`, so nothing here is
              // compiler-checked. Requiring the detail rather than its parent entry
              // routes that case to the internal-name fallback below instead of
              // throwing, which is what left this undefined for later renders.
              const itemGroupDetail = itemGroupContent?.commerceItemGroupDetail

              if (itemGroupDetail && itemContent) {
                const label = itemGroupDetail.retailNameSingular
                  ? itemGroupDetail.retailNameSingular
                  : itemGroupDetail.retailNamePlural

                result.push(
                  new CartBundleSelectionItemData(
                    label,
                    itemContent.retailName,
                    ci.upchargeCostPreDiscounts
                  )
                )
              } else {
                // Reached whenever the Contentful lookups above miss, which is the normal
                // degraded path rather than a crash — so this fallback must not carry an
                // assertion of its own.
                result.push(
                  new CartBundleSelectionItemData(
                    cartItemItemGroup.nameInternal,
                    cartItemItemGroupItem.item?.nameInternal ?? '',
                    ci.upchargeCostPreDiscounts
                  )
                )
              }
            }
          }
        })

        return result
      }
    },
    /**
     * The extended info line a cart row prints under a bundle's name, or `null` when the bundle has
     * none. Only the underclass background bundle has one, and it resolves the selected colour's
     * retail label through Contentful — so this reads the catalog and belongs here rather than on
     * `cart`, next to `cartBundleSelections` which the same two rows render.
     */
    cartBundleExtendedInfo(): (cartBundleId: string) => string | null {
      return (cartBundleId: string) => {
        const cartStore = useCartStore()

        const cartBundle = cartStore.cartData?.cartBundles.find(
          (cartBundle) => cartBundle.cartBundleId === cartBundleId
        )

        if (!cartBundle) {
          return null
        }

        if (cartBundle.bundleIdentifier === 'UC_Background') {
          return getUCBackgroundBundleExtendedInfo(cartBundle)
        }

        return null
      }
    },
    /**
     * Cart bundles still owed the selection upgrade (SUN-344): what cart/configure renders, minus
     * rewards, minus bundles whose configuration the customer has confirmed with Continue. Per bundle,
     * so a package added after an earlier confirmation is still owed its upgrade.
     */
    pendingSelectionUpgradeBundles(): CartBundleStorefrontDto[] {
      const confirmed = new Set(useMainStore().configurationConfirmedCartBundleIds)

      return upgradeableCartBundles(this.cartBundlesRequiringConfiguration).filter(
        (cartBundle) => !confirmed.has(cartBundle.cartBundleId)
      )
    },
    /**
     * Whether the selection upgrade would do anything right now. Shared by
     * `useSelectionUpgrade()`'s guard and `ConfigureView`'s `CadyLoading` gate, which is seeded
     * before first paint — so every clause here is synchronous. Does not check whether a pending
     * bundle is already at max price; an empty payload resolves in the same tick anyway.
     */
    shouldAttemptSelectionUpgrade(): boolean {
      const catalogStore = useCatalogStore()

      if (!featureFlags.selectionUpgradeOnCartConfigure) return false

      // No catalog means no `pricing.optionalItemPrices` to rank options by.
      if (catalogStore.catalogBundlesWithContent.length === 0) return false

      return this.pendingSelectionUpgradeBundles.length > 0
    },

    /**
     * Fresh `CartItemCustomization` instances for every cart bundle, seeded from the cart's prompt
     * responses and, for empty text prompts, the subject's personalisation defaults.
     *
     * A getter that returns a function rather than a cached value, on purpose: the editing store
     * (`cartConfiguration`) takes ownership of what this hands back and mutates it in place, so each
     * caller must receive new instances rather than a cached array whose members have already been
     * edited. Moved from `cartStore` as a plain action, where it had the same call-per-use shape.
     */
    generateCartItemCustomizations(): () => CartItemCustomization[] {
      return (): CartItemCustomization[] => {
        const mainStore = useMainStore()
        const cartStore = useCartStore()

        // Extracts the cart item customizations from the cart data
        const result: CartItemCustomization[] = []

        cartStore.cartData?.cartBundles.forEach((cartBundle) => {
          // find the cartItems that can be consolidated. (to prove this works, I'm only adding the first one for the moment).

          // Group the cart items by itemId
          const groupedCartItems = cartBundle.cartItems.reduce(
            (acc, cartItem) => {
              if (!acc[cartItem.itemId]) {
                acc[cartItem.itemId] = []
              }
              acc[cartItem.itemId].push(cartItem)
              return acc
            },
            {} as { [key: string]: CartItemStorefrontDto[] }
          )

          // loop through the grouped cart items
          Object.values(groupedCartItems).forEach((cartItems: CartItemStorefrontDto[]) => {
            if (cartItemCustomizationsCanBeGrouped(cartItems)) {
              const firstAndOnlyImagePrompt = cartItems[0].item?.prompts?.[0] as ImagePromptDto

              // We can consolidate these cart items into a single cart item
              const cartItemValuePairs: CartItemValuePair[] = []

              // loop over all the cart items and get the prompt response
              cartItems.forEach((cartItem: CartItemStorefrontDto) => {
                const promptResponse: Partial<ImagePromptResponseDto> | undefined =
                  cartItem.cartItemCustomizationDetails.promptResponses.find(
                    // we can use customizationPrompts[0] here b/c we know there is only one prompt.
                    (pr) => pr.promptId === cartItem.item?.prompts?.[0].promptId
                  )

                let theValue: MediaReferenceDto | null = null

                if (promptResponse && promptResponse.value) {
                  theValue = promptResponse.value as MediaReferenceDto
                }

                cartItemValuePairs.push(
                  new CartItemValuePair(
                    cartItem.cartItemId,
                    LocalPromptType.IMAGE,
                    firstAndOnlyImagePrompt.required,
                    theValue
                  )
                )
              })

              // just push one object into the result to represent the consolidated cart items.
              const multiSelectItemCustomization: MultiItemCustomization =
                new MultiItemCustomization(
                  firstAndOnlyImagePrompt.promptId,
                  firstAndOnlyImagePrompt.promptName,
                  cartItemValuePairs
                )

              result.push(
                new CartItemCustomization(
                  null, // since this is a new customization, we'll generate an id in the constructor
                  cartBundle.cartBundleId,
                  multiSelectItemCustomization,
                  null
                )
              )
            } else {
              cartItems.forEach((cartItem: CartItemStorefrontDto) => {
                const promptValuePairs: PromptValuePair[] = []

                if (cartItem.item?.prompts && cartItem.item?.prompts.length > 0) {
                  cartItem.item?.prompts.forEach((prompt: PromptDto) => {
                    const promptResponse:
                      | Partial<PromptResponseDto>
                      | Partial<ImagePromptResponseDto>
                      | undefined = cartItem.cartItemCustomizationDetails.promptResponses.find(
                      (pr) => pr.promptId === prompt.promptId
                    )

                    let theValue: string | MediaReferenceDto | null = null

                    if (
                      promptIsTextPrompt(prompt) ||
                      promptIsCheckboxListPrompt(prompt) ||
                      promptIsDropdownPrompt(prompt)
                    ) {
                      const pr: PromptResponseDto | null = promptResponse
                        ? (promptResponse as PromptResponseDto)
                        : null

                      if (pr && pr.value && pr.value.length > 0) {
                        theValue = pr.value
                      } else {
                        theValue = getPromptDefaultValue(prompt, mainStore.personalizationMap)
                      }
                    } else if (promptIsImagePrompt(prompt)) {
                      const pr = promptResponse as ImagePromptResponseDto

                      if (pr && pr.value) {
                        theValue = pr.value
                      }
                    }

                    let promptType = LocalPromptType.TEXT

                    if (promptIsImagePrompt(prompt)) {
                      promptType = LocalPromptType.IMAGE
                    }

                    promptValuePairs.push(
                      new PromptValuePair(
                        prompt.promptId,
                        prompt.promptName,
                        promptType,
                        prompt.required,
                        theValue
                      )
                    )
                  })

                  const singleItemCustomization: SingleItemCustomization =
                    new SingleItemCustomization(cartItem.cartItemId, promptValuePairs)

                  const cartItemCustomization = new CartItemCustomization(
                    null, // since this is a new customization, we'll generate an id in the constructor
                    cartBundle.cartBundleId,
                    null,
                    singleItemCustomization
                  )

                  result.push(cartItemCustomization)
                }
              })
            }
          })
        })

        return result
      }
    },
    /**
     * One selection per cart item, built from the cart itself so the config save can never omit an
     * item the customer still has. The server reconciles optional groups only, so a selection for an
     * included-group item is a no-op and is skipped when the group is known. The raw catalog (not
     * the content-joined list) supplies the group id; when the bundle is missing from the catalog
     * the selection is still emitted with no group. Returns a function for the same reason
     * `generateCartItemCustomizations` does.
     */
    generateCartItemSelections(): () => CartItemSelection[] {
      return (): CartItemSelection[] => {
        const cartStore = useCartStore()
        const result: CartItemSelection[] = []

        const catalogStore = useCatalogStore()

        cartStore.cartData?.cartBundles.forEach((cartBundle) => {
          const itemGroups =
            catalogStore.catalogData?.catalogBundles?.find(
              (cb) => cb.catalogBundleId === cartBundle.catalogBundleId
            )?.bundle?.itemGroups ?? []

          cartBundle.cartItems.forEach((cartItem) => {
            if (!cartItem.itemGroupItemId) return

            const itemGroup = itemGroups.find((ig) =>
              ig.itemGroupItems?.some((igi) => igi.itemGroupItemId === cartItem.itemGroupItemId)
            )

            if (itemGroup?.isIncluded) return

            result.push(
              new CartItemSelection(
                cartBundle.cartBundleId,
                itemGroup?.itemGroupId ?? null,
                cartItem.itemGroupItemId,
                cartItem.itemId
              )
            )
          })
        })

        return result
      }
    },

    /**
     * Active upsells for a catalog flow step, filtered and sorted for display.
     *
     * Once a cart exists its list is the only source: the catalog payload's is computed for an
     * empty cart and can offer what the cart already makes redundant. `GET cart` returns `null`
     * here (only mutations and `/prepare` compute it), so `refreshCart` keeps the previous list;
     * a cart with no list at all reads as none. Dismissals live in
     * `catalogStore.activeUpsellsDismissed`.
     */
    activeUpsellsForFlowStep(): (catalogFlowStepId: string) => ActiveUpsell[] {
      const cartStore = useCartStore()
      const catalogStore = useCatalogStore()

      return (catalogFlowStepId: string): ActiveUpsell[] => {
        const upsells = cartStore.cartData
          ? (cartStore.cartData.activeUpsells ?? [])
          : (catalogStore.catalogData?.activeUpsells ?? [])

        const result: ActiveUpsell[] = []

        for (const upsell of orderBy(upsells, 'zOrder', 'asc')) {
          if (upsell.catalogFlowStepId !== catalogFlowStepId) continue
          if (catalogStore.activeUpsellsDismissed.includes(upsell.catalogUpsellId)) continue

          // An upsell whose bundle has no Contentful content cannot be rendered, so it is dropped.
          const bundleWithContent = catalogStore.catalogBundlesWithContent.find(
            (b) => b.catalogBundleId === upsell.catalogBundleId
          )
          if (bundleWithContent) {
            result.push({ catalogUpsellId: upsell.catalogUpsellId, bundleWithContent })
          }
        }

        return result
      }
    },

    /**
     * Returns the combined commerce and catalog steps for navigation.
     * This merges pre-catalog steps, catalog flow steps, and post-catalog steps.
     */
    commerceAndCatalogSteps(): CommerceStep[] {
      const mainStore = useMainStore()
      const catalogStore = useCatalogStore()
      const cartStore = useCartStore()

      // Pre-catalog steps, filtered by applicability (e.g. Yearbook gating)
      const preCatalogSteps = buildPreCatalogSteps(
        [...mainStore.commerceSteps.preCatalog],
        mainStore.segmentationKey,
        mainStore.yearbookContext
      )

      // Catalog flow steps, excluding the retouching bundle's flow step
      const catalogSteps = buildCatalogFlowSteps(catalogStore, this.router, preCatalogSteps.length)

      // Splice the dedicated Package Builder step in immediately after the
      // catalog flow step that offers SheetSelection bundles. Position is
      // catalog-derived (the anchor flow step); existence is cart-derived (only
      // when the cart actually holds a builder bundle to configure). The
      // catalog steps carry `id === <flow step number>`, which is what the
      // anchor getter returns, so we match on that.
      if (this.cartHasPackageBuilderBundles) {
        const anchorNumber = catalogStore.packageBuilderAnchorFlowStepNumber
        if (anchorNumber !== null) {
          const anchorIndex = catalogSteps.findIndex((s) => s.id === anchorNumber.toString())
          if (anchorIndex !== -1) {
            catalogSteps.splice(anchorIndex + 1, 0, buildPackageBuilderStep(this.router))
          }
        }
      }

      // The Senior Preorder flow drives two step adjustments below: its
      // retouching is handled by a slide-up drawer (so the full-page RETOUCHING
      // step is dropped — otherwise the preorder all-digitals bundle trips
      // `cartContainsDigitalDownloadAll` and it reappears), and the Gallery step is
      // relabeled "Order for {name}" (the preorder page IS the order surface).
      const isSeniorPreorderActive = mainStore.isSeniorPreorderFlowActive

      // Post-catalog steps, filtered by applicability
      const postCatalogSteps = buildPostCatalogSteps(
        [...mainStore.commerceSteps.postCatalog],
        this.cartShouldSelectPoses,
        catalogStore.retouchingBundleId,
        mainStore.segmentationKey,
        cartStore.hasRetouchableMediaItemsInCart,
        cartStore.cartContainsDigitalDownloadAll,
        isSeniorPreorderActive
      )

      let result = [...preCatalogSteps, ...catalogSteps, ...postCatalogSteps]

      // Preorder: relabel the Gallery step "Order for {name}" everywhere the step nav
      // appears (preorder, checkout, …). Cloned rather than mutated so the shared
      // step definitions keep their original "Gallery" label for the normal flow.
      if (isSeniorPreorderActive) {
        const name = mainStore.subjectFullName
        if (name) {
          const label = substitute(commerceStepsContent.preorderGalleryStepNameTemplate, {
            SubjectName: name
          })
          result = result.map((step) =>
            step.id === PreCatalogStepId.GALLERY
              ? { ...step, shortName: label, longName: label }
              : step
          )
        }
      }

      // Assign final sequential step numbers
      result.forEach((step, index) => {
        step.number = index + 1
      })

      return result
    },

    /**
     * Returns the footer button label for the step after configuration:
     * either "Next: Retouching" or "Next: Checkout" depending on whether
     * the retouching step exists in the commerce flow.
     */
    retouchingOrCheckoutButtonLabel(): string {
      return substitute(commerceStepsContent.nextStepLabelTemplate, {
        StepName: this.retouchingCommerceStep
          ? commerceStepsContent.retouchingStepName
          : commerceStepsContent.checkoutStepShortName
      })
    },

    /**
     * Returns the retouching commerce step if it exists.
     */
    retouchingCommerceStep(): CommerceStep | undefined {
      const catalogStore = useCatalogStore()

      if (catalogStore.retouchingBundleId) {
        return this.commerceAndCatalogSteps.find((step) =>
          step.displaysBundleIds?.includes(catalogStore.retouchingBundleId as string)
        )
      }

      return undefined
    },

    /**
     * Returns the pose/background selection commerce step if it exists.
     */
    poseBackgroundSelectionCommerceStep(): CommerceStep | undefined {
      const catalogStore = useCatalogStore()

      if (catalogStore.poseBackgroundSelectionBundleId) {
        return this.commerceAndCatalogSteps.find((step) =>
          step.displaysBundleIds?.includes(catalogStore.poseBackgroundSelectionBundleId as string)
        )
      }

      return undefined
    },

    /**
     * Returns the commerce step for a given catalog flow step ID.
     *
     * Hands back the step itself rather than a `/catalog/flow/:n` route param derived from its
     * `number`. That derivation (`number - 1`) only held while exactly one pre-catalog step sat
     * ahead of the catalog section: with Senior's Yearbook step included, or the Package Builder
     * step spliced in mid-catalog, it pointed a step too far — and it collapsed a legitimate
     * first-step answer of `0` into the same falsy value callers read as "not found". The step's
     * own `routeUrl` is already the resolved href, so no arithmetic is needed.
     *
     * Matched case-insensitively: Contentful hands out upper-case `sunny16Guid`s while catalog
     * `catalogFlowStepId`s come through lower-case — the same mismatch
     * `catalogStore.isPoseBackgroundSelectionFlowStep` accounts for.
     */
    catalogStepByCatalogFlowStepId(): (catalogFlowStepId: string) => CommerceStep | undefined {
      const commerceAndCatalogStepsLocal = this.commerceAndCatalogSteps

      return (catalogFlowStepId: string) =>
        commerceAndCatalogStepsLocal.find(
          (step) => step.catalogFlowStepId?.toLowerCase() === catalogFlowStepId.toLowerCase()
        )
    },

    /**
     * Returns the first catalog flow step that displays bundles.
     */
    firstCatalogFlowStep(): CommerceStep | undefined {
      return (
        this.commerceAndCatalogSteps.find(
          (cacs) => cacs.displaysBundleIds && cacs.displaysBundleIds.length > 0
        ) ?? undefined
      )
    },

    /**
     * Returns the first catalog flow step that is a bundle list (not a special step like retouching).
     */
    firstBundleListCatalogFlowStep(): CommerceStep | undefined {
      const specialCatalogFlowStepIds: string[] = []

      if (this.retouchingCommerceStep) {
        specialCatalogFlowStepIds.push(this.retouchingCommerceStep.catalogFlowStepId as string)
      }

      if (this.poseBackgroundSelectionCommerceStep) {
        specialCatalogFlowStepIds.push(
          this.poseBackgroundSelectionCommerceStep.catalogFlowStepId as string
        )
      }

      return this.commerceAndCatalogSteps.find(
        (cacs) =>
          cacs.catalogFlowStepId !== null &&
          !specialCatalogFlowStepIds.includes(cacs.catalogFlowStepId)
      )
    }
  }
})

/**
 * Converts catalog flow steps into CommerceStep instances for navigation,
 * excluding flow steps that contain the retouching bundle.
 */
function buildCatalogFlowSteps(
  catalogStore: ReturnType<typeof useCatalogStore>,
  router: ReturnType<typeof useCommerceStore>['router'],
  preCatalogStepCount: number
): CommerceStep[] {
  if (!catalogStore.catalogHasFlowSteps || !catalogStore.catalogContent) {
    return []
  }

  return (
    catalogStore.catalogFlowStepsCombined
      // Exclude the flow step that contains the retouching bundle (it gets its own post-catalog step)
      .filter(
        (fs) =>
          !catalogStore.retouchingBundleId ||
          !fs.bundles.some((b) => b.bundleId === catalogStore.retouchingBundleId)
      )
      // Convert each CatalogFlowStep into a CommerceStep for the navigation
      .map((flowStep, index): CommerceStep => {
        // The step's position in `catalogFlowStepsCombined`, which `CatalogFlowLayout` indexes;
        // the filtered index would drift past an excluded retouching step
        const routeUrl = router.resolve({
          name: routeName.CATALOG_FLOW_STEP,
          params: { flowStep: flowStep.number }
        }).href

        return {
          id: flowStep.number.toString(),
          longName: flowStep.flowStepContent.name,
          shortName: flowStep.flowStepContent.shortName ?? flowStep.flowStepContent.name,
          catalogFlowStepId: flowStep.flowStepData?.catalogFlowStepId ?? null,
          routeUrl,
          // Placeholder number; overwritten by the caller after all sections are merged
          number: index + preCatalogStepCount + 1,
          displaysBundleIds: flowStep.bundles?.map((b) => b.bundleId) ?? []
        }
      })
  )
}

/**
 * Builds the dedicated Package Builder commerce step. Not backed by a catalog
 * flow step (`catalogFlowStepId: null`); it is spliced into the catalog section
 * after the anchor flow step by `commerceAndCatalogSteps`, which also assigns
 * the final `number`.
 */
function buildPackageBuilderStep(
  router: ReturnType<typeof useCommerceStore>['router']
): CommerceStep {
  return {
    id: PACKAGE_BUILDER_STEP_ID,
    longName: commerceStepsContent.packageBuilderStepName,
    shortName: commerceStepsContent.packageBuilderStepName,
    catalogFlowStepId: null,
    routeUrl: router.resolve({ name: routeName.PACKAGE_BUILDER }).href,
    number: null,
    displaysBundleIds: null
  }
}

/**
 * Whether the retouching step should be included in the commerce flow.
 * Requires a retouching bundle in the catalog, the subject's market + session
 * tier to be retouching-eligible, and either retouchable media items in the
 * cart or a "download all" bundle.
 */
function shouldIncludeRetouchingStep(
  retouchingBundleId: string | null,
  segmentationKey: SegmentationKey,
  hasRetouchableMediaItems: boolean,
  cartContainsDigitalDownloadAll: boolean
): boolean {
  if (retouchingBundleId === null) return false
  if (!hasRetouchableMediaItems && !cartContainsDigitalDownloadAll) return false

  switch (segmentationKey.market) {
    case CadyMarket.CapGown:
      return true
    case CadyMarket.Senior:
      // Senior subjects must carry sessionType per CoreAPI contract. A null read here
      // is a data error already reported by `mainStore.setSubject` (`stores/main.ts`) —
      // fail closed without duplicating the signal.
      if (segmentationKey.sessionType === null) return false
      return SENIOR_RETOUCHING_ELIGIBLE_SESSIONS.has(segmentationKey.sessionType)
    default:
      return false
  }
}

/**
 * Filters and configures post-catalog steps based on cart state and market.
 * Removes steps that aren't applicable (e.g. pose selection when not needed,
 * retouching when conditions aren't met) and assigns bundle IDs to the retouching step.
 */
function buildPostCatalogSteps(
  postCatalogSteps: CommerceStep[],
  cartShouldSelectPoses: boolean,
  retouchingBundleId: string | null,
  segmentationKey: SegmentationKey,
  hasRetouchableMediaItems: boolean,
  cartContainsDigitalDownloadAll: boolean,
  // Senior Preorder handles retouching via a drawer, so its full-page step is
  // suppressed even when it would otherwise qualify.
  retouchingHandledByDrawer: boolean
): CommerceStep[] {
  const includeRetouching =
    !retouchingHandledByDrawer &&
    shouldIncludeRetouchingStep(
      retouchingBundleId,
      segmentationKey,
      hasRetouchableMediaItems,
      cartContainsDigitalDownloadAll
    )

  return (
    postCatalogSteps
      // Remove steps that don't apply to the current cart/market state
      .filter((step) => {
        if (step.id === PostCatalogStepId.SELECT_POSES && !cartShouldSelectPoses) return false
        if (step.id === PostCatalogStepId.RETOUCHING && !includeRetouching) return false
        return true
      })
      // Assign the retouching bundle ID so the step knows which bundle it displays
      .map((step) => {
        if (step.id === PostCatalogStepId.RETOUCHING && retouchingBundleId) {
          step.displaysBundleIds = [retouchingBundleId]
        }
        return step
      })
  )
}

/**
 * Whether the Yearbook step should be included in the commerce flow.
 * Senior-only; further gated on session tier, the subject's last-photo date,
 * and whether every required pose already has a selection.
 *
 * Naming note: `SessionTypes.Yearbook` is the CoreAPI spelling of the
 * "Basic" session tier per the handoff — Basic-tier sessions skip the
 * Yearbook *page* even though the enum member shares its name.
 *
 * Visibility keys off selection *completeness*, not mere existence: the step
 * hides only when every required pose already has a selection. If any required
 * pose still lacks one — zero, or some-but-not-all (including poses
 * auto-selected server-side) — the step stays visible so the customer can fill
 * the gaps.
 */
function shouldIncludeYearbookStep(
  segmentationKey: SegmentationKey,
  yearbookContext: YearbookContext | null
): boolean {
  if (segmentationKey.market !== CadyMarket.Senior) return false
  if (yearbookContext === null) return false
  if (segmentationKey.sessionType === SessionTypes.Yearbook) return false
  if (
    yearbookContext.lastPhotoDate !== null &&
    new Date(yearbookContext.lastPhotoDate) < new Date()
  ) {
    return false
  }
  const selectionComplete =
    yearbookContext.selection !== null &&
    yearbookContext.selection.selectedImages.length ===
      yearbookContext.poseRequirements.requiredPoses.length
  if (selectionComplete) return false
  return true
}

/**
 * Filters pre-catalog steps by applicability. Mirrors `buildPostCatalogSteps`'s
 * shape — drop steps that don't apply for the current subject + state.
 */
function buildPreCatalogSteps(
  preCatalogSteps: CommerceStep[],
  segmentationKey: SegmentationKey,
  yearbookContext: YearbookContext | null
): CommerceStep[] {
  const includeYearbook = shouldIncludeYearbookStep(segmentationKey, yearbookContext)

  return preCatalogSteps.filter((step) => {
    if (step.id === PreCatalogStepId.YEARBOOK && !includeYearbook) return false
    return true
  })
}

function cartItemCustomizationsCanBeGrouped(cartItems: CartItemStorefrontDto[]) {
  return (
    cartItems.length > 1 && // there's more than one cart item
    cartItems[0].item?.prompts.length === 1 && // there's only one prompt
    promptIsImagePrompt(cartItems[0].item?.prompts[0]) // the only prompt is an image prompt
  )
}

function getUCBackgroundBundleExtendedInfo(
  underclassBackgroundCartBundle: CartBundleStorefrontDto
) {
  // This function assumes the cart bundle is the underclass background bundle and extracts the selected background color label
  const backgroundItem = getUCBackgroundItem(underclassBackgroundCartBundle)

  // No selection on the bundle — `getUCBackgroundItem` has already reported it
  if (!backgroundItem) {
    return null
  }

  // Get the prompt id from the prompt response
  const backgroundColorPromptId = backgroundItem.promptResponse.promptId

  // If there's no prompt id there's not enough info to proceed, so return null
  if (!backgroundColorPromptId) {
    return null
  }

  // Find the background color prompt in the cart item prompts. A response whose `promptId` matches
  // nothing on the item leaves this undefined — the old `as PromptDto` cast asserted otherwise and
  // then read `.dropdownItems` straight off it, which is the same crash as the one above a level up.
  const backgroundColorPrompt = backgroundItem.cartItem.item?.prompts.find(
    (p) => p.promptId === backgroundColorPromptId
  ) as DropdownPromptDto | undefined

  // Find the dropdown item that matches the selected background color value
  const bgColorDropdownItem = backgroundColorPrompt?.dropdownItems?.find(
    (di) => di.value === backgroundItem.value
  )

  if (!bgColorDropdownItem) {
    return null
  }

  const catalogStore = useCatalogStore()
  const promptContent = catalogStore.customizationPromptContent(backgroundColorPromptId)
  const colorOption = promptContent?.options?.find(
    (option) => option.sunny16Slug === bgColorDropdownItem.value
  )

  return colorOption?.label ?? bgColorDropdownItem.label
}
